import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BottomNavIcon } from "./BottomNavIcon";
import { Popup } from "./components/Popup";
import { PopupContent } from "./components/PopupContent";
import { Sidebar, SidebarSection, SidebarItem } from "./components/Sidebar";
import { POPUP_CONTENTS } from "./config/navigationConfig";

// Menu Items Configuration
const NAV_ITEMS = [
  { id: "access", label: "Access", iconName: "invite-access" },
  { id: "task", label: "Task", iconName: "task-management" },
  { id: "team", label: "Team", iconName: "team-management" },
  { id: "sample", label: "Sample", iconName: "sample-dispatch" },
  { id: "more", label: "More", iconName: "more" },
];

export function BottomNavbar() {
  const [activeTab, setActiveTab] = useState("access");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 375
  );

  // Track window width for dynamic SVG sizing
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate the position of the "dip" based on the active tab index
  const activeIndex = NAV_ITEMS.findIndex((item) => item.id === activeTab);

  // Dynamic item width based on current window width
  const itemWidth = windowWidth / NAV_ITEMS.length;
  const centerPos = activeIndex * itemWidth + itemWidth / 2;

  const handleTabClick = (id) => {
    const config = POPUP_CONTENTS[id];

    if (config?.type === "sidebar") {
      // For "More" button, toggle sidebar
      if (activeTab === id) {
        setIsSidebarOpen(!isSidebarOpen);
      } else {
        setActiveTab(id);
        setIsSidebarOpen(true);
        setIsPopupOpen(false);
      }
    } else {
      // For other buttons, toggle popup
      if (activeTab === id) {
        setIsPopupOpen(!isPopupOpen);
        setIsSidebarOpen(false);
      } else {
        setActiveTab(id);
        setIsPopupOpen(true);
        setIsSidebarOpen(false);
      }
    }
  };

  // --- SVG PATHS ---
  // Symmetric Design with "Circle Edge" look.
  const curveWidth = 118;
  const curveDepth = 32;
  const cpOffset = curveWidth / 4; // Adjusted for rounder curve

  const navbarPath = `
    M 0 0
    L ${centerPos - curveWidth / 2} 0
    C ${centerPos - curveWidth / 2 + cpOffset} 0, ${
    centerPos - cpOffset
  } ${curveDepth}, ${centerPos} ${curveDepth}
    C ${centerPos + cpOffset} ${curveDepth}, ${
    centerPos + curveWidth / 2 - cpOffset
  } 0, ${centerPos + curveWidth / 2} 0
    L ${windowWidth} 0
    L ${windowWidth} 80
    L 0 80
    Z
  `;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col justify-end pointer-events-none md:hidden">
      {/* Popup Layer */}
      <AnimatePresence>
        {isPopupOpen && POPUP_CONTENTS[activeTab]?.type === "popup" && (
          <Popup
            isOpen={isPopupOpen}
            windowWidth={windowWidth}
            centerPos={centerPos}
            curveWidth={curveWidth}
            curveDepth={curveDepth}
            height={240}
            horizontalPadding={8}
          >
            <PopupContent
              title={POPUP_CONTENTS[activeTab].title}
              items={POPUP_CONTENTS[activeTab].items}
            />
          </Popup>
        )}
      </AnimatePresence>

      {/* Sidebar for "More" button */}
      <AnimatePresence>
        {isSidebarOpen && POPUP_CONTENTS[activeTab]?.type === "sidebar" && (
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            windowWidth={windowWidth}
            centerPos={centerPos}
            curveWidth={curveWidth}
            curveDepth={curveDepth}
          >
            {POPUP_CONTENTS[activeTab].sections?.map((section, index) => (
              <SidebarSection key={index} title={section.title}>
                {section.items.map((item, itemIndex) => (
                  <SidebarItem
                    key={itemIndex}
                    label={item.label}
                    icon={item.icon}
                    onClick={() => {
                      item.onClick?.();
                      setIsSidebarOpen(false);
                    }}
                  />
                ))}
              </SidebarSection>
            ))}
          </Sidebar>
        )}
      </AnimatePresence>

      {/* Navbar Layer */}
      <div className="relative w-full h-[80px] bg-transparent pointer-events-auto z-50">
        {/* Navbar Background SVG */}
        {/* drop-shadow-[0_-5px_15px_rgba(0,0,0,0.05)] dark:drop-shadow-[0_-5px_15px_rgba(0,0,0,0.3)] */}
        <div className="absolute inset-0 w-full h-full ">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${windowWidth} 80`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <motion.path
              d={navbarPath}
              className="fill-background"
              animate={{ d: navbarPath }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </svg>
        </div>

        {/* Floating Action Button */}
        {/* Positioned at top: -36px. 
            Button height 56px. Center at 28px from top.
            -36 + 28 = -8px (8px above navbar top).
            This aligns perfectly with the gap center (mb-4 = 16px gap).
        */}
        <motion.div
          className="absolute -top-[30px] flex items-center justify-center w-[56px] h-[56px] bg-primary rounded-full text-white shadow-lg z-50"
          style={{ left: 0 }}
          animate={{ x: centerPos - 28 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {NAV_ITEMS.map((item) => {
            return activeTab === item.id ? (
              <BottomNavIcon
                key={item.id}
                name={item.iconName}
                className="w-6 h-6"
              />
            ) : null;
          })}
        </motion.div>

        {/* Navigation Items */}
        <div className="absolute inset-0 flex items-end justify-between px-0 pb-0 h-[96%]">
          {NAV_ITEMS.map((item) => {
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className={`flex flex-col items-center justify-end w-1/5 pb-4 transition-colors duration-300 z-50 ${
                  isActive ? "text-transparent" : "text-foreground"
                }`}
                style={{ height: "80px" }}
              >
                <div
                  className={`mb-1 transition-opacity duration-200 ${
                    isActive ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <BottomNavIcon name={item.iconName} className="w-6 h-6" />
                </div>
                <span
                  className={`text-[12px] font-normal transition-all duration-200 ${
                    isActive
                      ? "text-primary translate-y-1 font-bold scale-130"
                      : "text-foreground scale-100"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
